import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";

import {
  captureBlock,
  captureFromScoreText,
  captureToScoreText,
  emptyCaptureSet,
  maxSets,
  setChipFromWinner,
  setIssues,
  setLabel,
  setWinnerSide,
  showDeciderCard,
  tiebreakApplies,
  type CapturePlayer,
  type CaptureSet,
  type CaptureSetKind,
  type CaptureState,
} from "../lib/resultCapture";
import { setPairHasTiebreak } from "../lib/scoreDraft";

/** Captura de resultado set por set (maqueta aprobada por Eduardo
 * 2026-10-06): 1) ¿Quién ganó?; 2) un set por tarjeta con un renglón por
 * jugador, − / +, «ganó el set» automático, Normal/Súper muerte y tie-break
 * solo con 7-6; 3) «Así queda» desde quien ganó y la hoja «¿Así fue?» antes
 * de mandar. La usan el jugador y el admin («Editar»): el texto que se manda
 * es el de siempre (desde el jugador 1). */

export interface ResultSetCapturePayload {
  score_text: string | null;
  winner_participant_id: number | null;
  notes: string | null;
}

interface Props {
  player1: CapturePlayer;
  player2: CapturePlayer;
  pickleball: boolean;
  initialScoreText?: string | null;
  initialWinnerId?: number | null;
  initialNotes?: string | null;
  busy?: boolean;
  /** Prefijo único para ids (varias capturas en la misma página). */
  idPrefix: string;
  showNotes?: boolean;
  /** Texto bajo el resultado en «¿Así fue?». */
  confirmNote: string;
  confirmLabel?: string;
  submitLabel?: string;
  onSubmit: (payload: ResultSetCapturePayload) => Promise<void> | void;
  onCancel?: () => void;
}

function initials(name: string): string {
  const parts = name.split(/[\s/]+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

function step(value: string, delta: number): string {
  const current = Number(value.trim() || "0");
  const next = Math.min(99, Math.max(0, (Number.isFinite(current) ? current : 0) + delta));
  return String(next);
}

function clean(value: string): string {
  return value.replace(/[^0-9]/g, "").slice(0, 2);
}

export default function ResultSetCapture({
  player1,
  player2,
  pickleball,
  initialScoreText,
  initialWinnerId,
  initialNotes,
  busy = false,
  idPrefix,
  showNotes = true,
  confirmNote,
  confirmLabel = "Sí, enviar",
  submitLabel = "Revisar y enviar",
  onSubmit,
  onCancel,
}: Props) {
  const players: [CapturePlayer, CapturePlayer] = [player1, player2];
  const initialKey = `${initialScoreText ?? ""}|${initialWinnerId ?? ""}|${initialNotes ?? ""}`;
  const [state, setState] = useState<CaptureState>(() =>
    captureFromScoreText(initialScoreText, initialWinnerId, pickleball, initialNotes ?? ""),
  );
  const [confirming, setConfirming] = useState(false);
  const confirmRef = useRef<HTMLElement | null>(null);
  const reviewButtonRef = useRef<HTMLButtonElement | null>(null);
  const wasConfirming = useRef(false);

  // Si el reporte de origen cambia (p. ej. tras enviar), se vuelve a precargar.
  // Se ajusta durante el render (no en un efecto): un efecto al montar corría
  // DESPUÉS de pintar y podía borrar el primer toque («¿Quién ganó?») si caía
  // antes de que React vaciara los efectos (prueba intermitente en CI).
  const preloadKey = `${initialKey}|${pickleball ? "p" : "t"}`;
  const [loadedKey, setLoadedKey] = useState(preloadKey);
  if (loadedKey !== preloadKey) {
    setLoadedKey(preloadKey);
    setState(captureFromScoreText(initialScoreText, initialWinnerId, pickleball, initialNotes ?? ""));
    setConfirming(false);
  }

  // Hoja «¿Así fue?» (revisión 2026-10-06): al abrir, el foco entra a la
  // hoja; al cerrar, regresa a «Revisar y enviar».
  useEffect(() => {
    if (confirming) {
      confirmRef.current?.focus();
    } else if (wasConfirming.current) {
      reviewButtonRef.current?.focus();
    }
    wasConfirming.current = confirming;
  }, [confirming]);

  function onSheetKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      if (!busy) setConfirming(false);
      return;
    }
    if (event.key !== "Tab" || !confirmRef.current) return;
    // Foco encerrado: Tab y Mayús+Tab dan la vuelta dentro de la hoja.
    const focusable = Array.from(
      confirmRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input:not([disabled])'),
    );
    if (!focusable.length) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === confirmRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  const unit = pickleball ? "game" : "set";
  const block = useMemo(() => captureBlock(state, players, pickleball), [state, players, pickleball]);
  const winnerSide: 1 | 2 | null = state.winner === null ? null : state.winner === player1.id ? 1 : 2;
  const winnerName = winnerSide === 1 ? player1.name : winnerSide === 2 ? player2.name : null;
  const loserName = winnerSide === 1 ? player2.name : winnerSide === 2 ? player1.name : null;
  const filledSets = state.sets.filter((set) => set.p1.trim() || set.p2.trim());
  const chips = winnerSide ? filledSets.map((set) => setChipFromWinner(set, winnerSide, pickleball)) : [];
  const limit = maxSets(pickleball);

  function update(index: number, patch: Partial<CaptureSet>) {
    setState((prev) => ({
      ...prev,
      sets: prev.sets.map((set, i) => {
        if (i !== index) return set;
        const next = { ...set, ...patch };
        // Revisión 2026-10-06: si el set deja de ser 7-6/6-7, su tie-break
        // se borra (no se manda un tie-break que ya no se ve).
        const gamesChanged = "p1" in patch || "p2" in patch;
        if (gamesChanged && !setPairHasTiebreak(next.p1.trim(), next.p2.trim())) {
          next.tb1 = "";
          next.tb2 = "";
        }
        return next;
      }),
    }));
  }

  function setKind(index: number, kind: CaptureSetKind) {
    update(index, { kind, tb1: "", tb2: "" });
  }

  function addSet(kind: CaptureSetKind) {
    setState((prev) => (prev.sets.length >= limit ? prev : { ...prev, sets: [...prev.sets, emptyCaptureSet(kind)] }));
  }

  function removeLast() {
    setState((prev) => (prev.sets.length <= 1 ? prev : { ...prev, sets: prev.sets.slice(0, -1) }));
  }

  function focusFirstSet() {
    const input = document.getElementById(`${idPrefix}-set-0-p1`) as HTMLInputElement | null;
    input?.focus();
  }

  const submitText =
    block.kind === "ok"
      ? submitLabel
      : block.kind === "no_decider"
        ? `Falta el ${unit} que define`
        : block.kind === "no_winner"
          ? "Marca quién ganó"
          : block.kind === "empty_sets"
            ? "Falta llenar el marcador"
            : "Corrige lo marcado para enviar";

  async function send() {
    const scoreText = captureToScoreText(state, pickleball);
    await onSubmit({
      score_text: scoreText || null,
      winner_participant_id: state.winner,
      notes: state.notes.trim() || null,
    });
    setConfirming(false);
  }

  return (
    <div className="rsc" role="group" aria-label="Reportar resultado">
      <section className="rsc__section" aria-labelledby={`${idPrefix}-quien`}>
        <h3 id={`${idPrefix}-quien`} className="rsc__step">
          <span aria-hidden="true" className="rsc__step-num">1</span>¿Quién ganó?
        </h3>
        {players.map((player) => {
          const pressed = state.winner === player.id;
          return (
            <button
              key={player.id}
              type="button"
              className={`rsc__winner${pressed ? " is-pressed" : ""}`}
              aria-pressed={pressed}
              disabled={busy}
              onClick={() => setState((prev) => ({ ...prev, winner: player.id }))}
            >
              <span aria-hidden="true" className="rsc__avatar">{initials(player.name)}</span>
              <span className="rsc__winner-name">{player.name}</span>
              {pressed ? (
                <svg className="rsc__check" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12.5l4.5 4.5L19 7.5" />
                </svg>
              ) : null}
            </button>
          );
        })}
        <label className="rsc__type">
          Tipo de resultado
          <select
            value={state.resultType}
            disabled={busy}
            onChange={(e) => setState((prev) => ({ ...prev, resultType: e.target.value as CaptureState["resultType"] }))}
          >
            <option value="normal">Normal</option>
            <option value="default">Default</option>
            <option value="retiro">Retiro</option>
          </select>
        </label>
      </section>

      <section className="rsc__section" aria-labelledby={`${idPrefix}-marcador`}>
        <div>
          <h3 id={`${idPrefix}-marcador`} className="rsc__step">
            <span aria-hidden="true" className="rsc__step-num">2</span>Marcador, {unit} por {unit}
          </h3>
          <p className="rsc__hint">
            {pickleball ? "Los puntos de cada quien, en su propio renglón." : "Los juegos de cada quien, en su propio renglón."}
          </p>
        </div>

        {state.sets.map((set, index) => {
          const isSuper = !pickleball && set.kind === "super";
          // Un set vacío no se marca en rojo (la captura empieza vacía): solo
          // detiene el botón de enviar.
          const issues = setIssues(set, index, players, pickleball, state.resultType, index === state.sets.length - 1).filter(
            (issue) => issue.kind !== "empty",
          );
          const side = setWinnerSide(set);
          const isLast = index === state.sets.length - 1;
          const label = setLabel(index, set, pickleball);
          return (
            <div key={index} className={`rsc__set${isSuper ? " is-super" : ""}${issues.length ? " has-issue" : ""}`}>
              <div className="rsc__set-head">
                <span className="rsc__set-title">
                  <span className="rsc__set-name">{isSuper ? `${label} · Súper muerte` : label}</span>
                  {isSuper ? <span className="rsc__set-sub">a 10 puntos, gana por 2</span> : null}
                </span>
                <span className="rsc__set-tools">
                  {!pickleball ? (
                    <span role="group" aria-label={`Tipo del ${label.toLowerCase()}`} className="rsc__kind">
                      <button type="button" aria-pressed={!isSuper} disabled={busy} onClick={() => setKind(index, "normal")}>
                        Normal
                      </button>
                      <button type="button" aria-pressed={isSuper} disabled={busy} onClick={() => setKind(index, "super")}>
                        Súper muerte
                      </button>
                    </span>
                  ) : null}
                  {isLast && state.sets.length > 1 ? (
                    <button type="button" className="rsc__remove" disabled={busy} onClick={removeLast}>
                      Quitar
                    </button>
                  ) : null}
                </span>
              </div>
              {players.map((player, playerIndex) => {
                const field = playerIndex === 0 ? "p1" : "p2";
                const won = side === playerIndex + 1;
                const unitWord = isSuper || pickleball ? "punto" : "juego";
                const inputLabel = isSuper
                  ? `Puntos de ${player.name} en la súper muerte (${label.toLowerCase()})`
                  : `${pickleball ? "Puntos" : "Juegos"} de ${player.name} en el ${label.toLowerCase()}`;
                return (
                  <div key={player.id} className={`rsc__row${won ? " is-won" : ""}`}>
                    <span className="rsc__row-name">
                      {player.name}
                      {won ? <span className="rsc__won"> {isSuper ? "ganó la súper muerte" : `ganó el ${unit}`}</span> : null}
                    </span>
                    <button
                      type="button"
                      className="rsc__step-btn"
                      aria-label={`Quitar un ${unitWord} a ${player.name} (${label.toLowerCase()})`}
                      disabled={busy}
                      onClick={() => update(index, { [field]: step(set[field], -1) })}
                    >
                      −
                    </button>
                    <input
                      id={`${idPrefix}-set-${index}-${field}`}
                      className="rsc__num"
                      type="number"
                      min="0"
                      max="99"
                      inputMode="numeric"
                      aria-label={inputLabel}
                      value={set[field]}
                      disabled={busy}
                      onChange={(e) => update(index, { [field]: clean(e.target.value) })}
                    />
                    <button
                      type="button"
                      className="rsc__step-btn"
                      aria-label={`Sumar un ${unitWord} a ${player.name} (${label.toLowerCase()})`}
                      disabled={busy}
                      onClick={() => update(index, { [field]: step(set[field], 1) })}
                    >
                      +
                    </button>
                  </div>
                );
              })}
              {tiebreakApplies(set, pickleball) ? (
                <div className="rsc__tb">
                  <span className="rsc__tb-title">
                    Tie-break de este set <span className="rsc__tb-sub">· sale solo con 7-6</span>
                  </span>
                  {players.map((player, playerIndex) => {
                    const field = playerIndex === 0 ? "tb1" : "tb2";
                    return (
                      <label key={player.id} className="rsc__tb-row">
                        <span>{player.name}</span>
                        <input
                          className="rsc__num rsc__num--tb"
                          type="number"
                          min="0"
                          max="99"
                          inputMode="numeric"
                          aria-label={`Puntos de tie-break de ${player.name} en el ${label.toLowerCase()}`}
                          value={set[field]}
                          disabled={busy}
                          onChange={(e) => update(index, { [field]: clean(e.target.value) })}
                        />
                      </label>
                    );
                  })}
                </div>
              ) : null}
              {issues.map((issue) => (
                <div key={issue.kind} role="alert" className="rsc__alert">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 4l9 16H3z" />
                    <path d="M12 10v4M12 17h.01" />
                  </svg>
                  <div>
                    <p>{issue.message}</p>
                    {issue.fixToNormal ? (
                      <button type="button" className="btn btn--ghost btn--compact" disabled={busy} onClick={() => setKind(index, "normal")}>
                        Sí, cambiar a set normal
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          );
        })}

        {showDeciderCard(state, pickleball) ? (
          <div className="rsc__decider">
            <div>
              <span className="rsc__decider-title">Van 1-1. ¿Cómo se definió?</span>
              <span className="rsc__decider-sub">Agrega el {unit} que decidió el partido.</span>
            </div>
            {!pickleball ? (
              <button type="button" className="rsc__add" disabled={busy} onClick={() => addSet("super")}>
                <span aria-hidden="true">+</span>
                <span>
                  <span className="rsc__add-title">Súper muerte</span>
                  <span className="rsc__add-sub">a 10 puntos, gana por 2</span>
                </span>
              </button>
            ) : null}
            <button type="button" className="rsc__add" disabled={busy} onClick={() => addSet("normal")}>
              <span aria-hidden="true">+</span>
              <span>
                <span className="rsc__add-title">{pickleball ? "Tercer game" : "Tercer set normal"}</span>
                <span className="rsc__add-sub">{pickleball ? "a 11 puntos" : "a 6 juegos"}</span>
              </span>
            </button>
          </div>
        ) : !pickleball && state.sets.length < limit ? (
          <div className="rsc__more">
            <span className="rsc__more-title">¿Se jugó otro set?</span>
            <div className="rsc__more-actions">
              <button type="button" className="btn btn--ghost btn--compact" disabled={busy} onClick={() => addSet("normal")}>
                + Set normal
              </button>
              <button type="button" className="btn btn--ghost btn--compact" disabled={busy} onClick={() => addSet("super")}>
                + Súper muerte
              </button>
            </div>
            <span className="rsc__hint">Si te equivocaste, quita el último set con «Quitar» en su tarjeta.</span>
          </div>
        ) : null}
      </section>

      {showNotes ? (
        <label className="rsc__notes">
          Notas (opcional)
          <textarea
            rows={2}
            value={state.notes}
            disabled={busy}
            onChange={(e) => setState((prev) => ({ ...prev, notes: e.target.value }))}
          />
        </label>
      ) : null}

      <section className={`rsc__summary${block.kind === "winner_mismatch" ? " is-warning" : ""}`} aria-label="Así queda el resultado">
        {block.kind === "winner_mismatch" ? (
          <>
            <span className="rsc__summary-label">REVISA ANTES DE ENVIAR</span>
            <p>
              Según los sets ganó <strong>{players[block.impliedSide - 1].name}</strong>, pero marcaste a {winnerName} como ganador.
            </p>
            <div className="rsc__summary-actions">
              <button
                type="button"
                className="btn btn--compact"
                disabled={busy}
                onClick={() => setState((prev) => ({ ...prev, winner: players[block.impliedSide - 1].id }))}
              >
                Ganó {players[block.impliedSide - 1].name}
              </button>
              <button type="button" className="btn btn--ghost btn--compact" disabled={busy} onClick={focusFirstSet}>
                Corregir los sets
              </button>
            </div>
          </>
        ) : block.kind === "no_decider" ? (
          <>
            <span className="rsc__summary-label">ASÍ VA</span>
            <p>Todavía no hay ganador: van 1-1 en {unit}s.</p>
          </>
        ) : block.kind === "no_winner" ? (
          <>
            <span className="rsc__summary-label">ASÍ VA</span>
            <p>Falta marcar quién ganó.</p>
            {block.impliedSide ? (
              <div className="rsc__summary-actions">
                <button
                  type="button"
                  className="btn btn--compact"
                  disabled={busy}
                  onClick={() => setState((prev) => ({ ...prev, winner: players[(block.impliedSide as 1 | 2) - 1].id }))}
                >
                  Ganó {players[block.impliedSide - 1].name}
                </button>
              </div>
            ) : null}
          </>
        ) : block.kind === "ok" && winnerName && loserName ? (
          <>
            <span className="rsc__summary-label">ASÍ QUEDA</span>
            <p className="rsc__summary-result">
              {winnerName} le ganó a {loserName}
              {state.resultType === "retiro" ? " (retiro)" : state.resultType === "default" ? " (default)" : ""}
            </p>
            {chips.length ? (
              <div className="rsc__chips">
                {chips.map((chip, index) => (
                  <span key={index} className="rsc__chip">
                    {chip}
                  </span>
                ))}
              </div>
            ) : null}
            <p className="rsc__hint">Desde el lado de quien ganó.</p>
          </>
        ) : (
          <>
            <span className="rsc__summary-label">ASÍ VA</span>
            <p>{block.kind === "empty_sets" ? `Llena el marcador de cada ${unit} (o quita el que sobra).` : `Corrige lo marcado en los ${unit}s.`}</p>
          </>
        )}
      </section>

      <div className="form__actions">
        <button
          ref={reviewButtonRef}
          className="btn rsc__submit"
          type="button"
          disabled={busy || block.kind !== "ok"}
          onClick={() => setConfirming(true)}
        >
          {submitText}
        </button>
        {onCancel ? (
          <button className="btn btn--ghost" type="button" disabled={busy} onClick={onCancel}>
            Cancelar
          </button>
        ) : null}
      </div>

      {/* La hoja va en un portal a document.body: dentro de la tabla del admin
          («Editar») la celda sticky crea un contexto de apilamiento y el
          z-index de la hoja quedaba atrapado detrás de la barra inferior. */}
      {confirming && winnerName && loserName && winnerSide && typeof document !== "undefined" ? createPortal(
        <div className="rsc__sheet-wrap">
          <div className="rsc__sheet-backdrop" aria-hidden="true" onClick={() => setConfirming(false)} />
          <section
            className="rsc__sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${idPrefix}-confirmar`}
            tabIndex={-1}
            onKeyDown={onSheetKeyDown}
            ref={(node) => {
              confirmRef.current = node;
            }}
          >
            <h2 id={`${idPrefix}-confirmar`}>¿Así fue?</h2>
            <div className="rsc__sheet-body">
              <p className="rsc__summary-result">
                {winnerName} le ganó a {loserName}
                {state.resultType === "retiro" ? " (retiro)" : state.resultType === "default" ? " (default)" : ""}
              </p>
              {chips.length ? (
                <div className="rsc__chips">
                  {chips.map((chip, index) => (
                    <span key={index} className="rsc__chip">
                      {chip}
                    </span>
                  ))}
                </div>
              ) : null}
              <ul className="rsc__sheet-list">
                {filledSets.map((set, index) => {
                  const sideWon = setWinnerSide(set) ?? winnerSide;
                  const first = players[sideWon - 1];
                  const second = players[2 - sideWon];
                  const firstVal = sideWon === 1 ? set.p1 : set.p2;
                  const secondVal = sideWon === 1 ? set.p2 : set.p1;
                  const isSuper = !pickleball && set.kind === "super";
                  const head = isSuper ? "Súper muerte" : setLabel(index, set, pickleball);
                  const hasTb = tiebreakApplies(set, pickleball) && (set.tb1.trim() || set.tb2.trim());
                  const tbFirst = sideWon === 1 ? set.tb1 : set.tb2;
                  const tbSecond = sideWon === 1 ? set.tb2 : set.tb1;
                  return (
                    <li key={index}>
                      {head}: {first.name} {firstVal.trim() || "0"}, {second.name} {secondVal.trim() || "0"}
                      {hasTb ? ` · tie-break: ${first.name} ${tbFirst.trim() || "?"}, ${second.name} ${tbSecond.trim() || "?"}` : ""}
                    </li>
                  );
                })}
              </ul>
            </div>
            <p className="rsc__hint">{confirmNote}</p>
            <div className="rsc__sheet-actions">
              <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => setConfirming(false)}>
                Corregir
              </button>
              <button type="button" className="btn" disabled={busy} onClick={() => void send()}>
                {confirmLabel}
              </button>
            </div>
          </section>
        </div>,
        document.body,
      ) : null}
    </div>
  );
}
